import {
  createRouter,
  createRoute,
  createRootRoute,
  RouterProvider,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { AppLayout } from "@layouts/AppLayout";
import { AuthLayout } from "@layouts/AuthLayout";
import { LoginPage } from "@modules/auth/pages/LoginPage";
import { AdminPanelPage } from "@/modules/admin-panel/pages/AdminPanelPage";
import { BillingDashboardPage } from "@modules/billing/pages/BillingDashboardPage";
import { IssuersListPage } from "@modules/billing/pages/IssuersListPage";
import { FacturasListPage } from "@modules/billing/pages/FacturasListPage";
import { FacturaFormPage } from "@modules/billing/pages/FacturaFormPage";
import { ClientesListPage } from "@modules/billing/pages/ClientesListPage";
import { ConceptosListPage } from "@modules/billing/pages/ConceptosListPage";
import { PlanCuentasPage } from "@modules/accounting/pages/PlanCuentasPage";
import { DiarioPage } from "@modules/accounting/pages/DiarioPage";
import { MayorPage } from "@modules/accounting/pages/MayorPage";
import { InformesPage } from "@modules/accounting/pages/InformesPage";
import { Modelo303Page } from "@modules/accounting/pages/Modelo303Page";
import { ConfigContablePage } from "@modules/accounting/pages/ConfigContablePage";
import { MarketingDashboardPage } from "@modules/marketing/pages/MarketingDashboardPage";
import { ContactosPage } from "@modules/marketing/pages/ContactosPage";
import { PipelinePage } from "@modules/marketing/pages/PipelinePage";
import { CampanasPage } from "@modules/marketing/pages/CampanasPage";
import { ContenidosPage } from "@modules/marketing/pages/ContenidosPage";
import { AutomatizacionesPage } from "@modules/marketing/pages/AutomatizacionesPage";
import { ScoringPage } from "@modules/marketing/pages/ScoringPage";
import { FormulariosPage } from "@modules/marketing/pages/FormulariosPage";
import { FormularioPublicoPage } from "@modules/marketing/pages/FormularioPublicoPage";
import { AnalyticsPage } from "@modules/marketing/pages/AnalyticsPage";
import { AgendaPage } from "@modules/marketing/pages/AgendaPage";
import { RolesPermisosPage } from "@modules/settings/pages/RolesPermisosPage";
import { AuditoriaPage } from "@modules/settings/pages/AuditoriaPage";
import { PresupuestosPage } from "@modules/billing/pages/PresupuestosPage";
import { ComprasPage } from "@modules/purchases/pages/ComprasPage";
import { EquipoPage } from "@modules/settings/pages/EquipoPage";
import { ConfiguracionPage } from "@modules/settings/pages/ConfiguracionPage";
import { MarcaPage } from "@modules/settings/pages/MarcaPage";
import { RrhhConfigPage } from "@modules/settings/pages/RrhhConfigPage";
import { FacturacionConfigPage } from "@modules/settings/pages/FacturacionConfigPage";
import { DataCleanupPage } from "@modules/settings/pages/DataCleanupPage";
import { OrganizationPage } from "@modules/settings/pages/OrganizationPage";
import { AssistantPage } from "@modules/ai-assistant/pages/AssistantPage";
import { useAuthStore } from "@shared/store/auth";
import { requirePermission, requireRole } from "./guards";
import { RrhhPage } from "@modules/rrhh/pages/RrhhPage";
import { ItPage } from "@modules/it/pages/ItPage";
import { ProfilePage } from "@modules/profile/pages/ProfilePage";
import { TasksPage } from "@modules/tasks/pages/TasksPage";
import { ProyectosListPage } from "@modules/proyectos/pages/ProyectosListPage";
import { ProyectoDetailPage } from "@modules/proyectos/pages/ProyectoDetailPage";
import { AutomationPage } from "@modules/automation/pages/AutomationPage";
import { CommercialDashboardPage } from "@modules/commercial/pages/CommercialDashboardPage";
import { CommercialValuationsPage } from "@modules/commercial/pages/CommercialValuationsPage";
import { CommercialCostsPage } from "@modules/commercial/pages/CommercialCostsPage";
import { CommercialBenefitsPage } from "@modules/commercial/pages/CommercialBenefitsPage";
import { CommercialSettingsPage } from "@modules/commercial/pages/CommercialSettingsPage";
import { NotificationsPage } from "@modules/notifications/pages/NotificationsPage";
import { CertificacionesPage } from "@modules/certificaciones/pages/CertificacionesPage";
import { BibliotecaPage } from "@modules/certificaciones/biblioteca/pages/BibliotecaPage";
import { BibliotecaDocumentosPage } from "@modules/certificaciones/biblioteca/pages/BibliotecaDocumentosPage";
import { Iso900114001Page } from "@modules/certificaciones/standards/iso-9001-14001/pages/Iso900114001Page";
import { Iso2700127701Page } from "@modules/certificaciones/standards/iso-27001-27701/pages/Iso2700127701Page";
import { NcpSignaturePage } from "@modules/certificaciones/shared/ncp-signature/pages/NcpSignaturePage";

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="p-8">
      <div className="rounded-3xl border border-slate-200/70 bg-white p-8 shadow-soft">
        <div className="text-xs font-black uppercase tracking-[0.2em] text-emerald-700">
          Módulo
        </div>
        <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">
          {title}
        </h1>
        <p className="mt-2 max-w-2xl text-sm font-semibold text-slate-500">
          Esta sección está preparada en la navegación principal y se irá
          desarrollando por módulos.
        </p>
      </div>
    </div>
  );
}

// Permisos que dan acceso al Panel de administración (gestión de personas/sistema).
const PANEL_PERMS = ["view-employees", "manage-roles"];
const ISO_9001_14001_ROUTE = "/certificaciones/iso-9001-14001" as const;

function redirectToIso900114001() {
  throw redirect({ to: ISO_9001_14001_ROUTE });
}

function HomeRedirect() {
  const me = useAuthStore((s) => s.me);
  const perms = me?.permissions ?? [];

  if (PANEL_PERMS.some((p) => perms.includes(p))) {
    return <AdminPanelPage />;
  }

  return <RrhhPage />;
}

const adminPanelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin-panel",
  beforeLoad: requirePermission(PANEL_PERMS, "/rrhh"),
  component: AdminPanelPage,
});

// --- Root ---
const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </>
  ),
});

// --- Auth-protected layout ---
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async () => {
    const isAuth = await useAuthStore.getState().ensureLoaded();
    if (!isAuth) {
      throw redirect({ to: "/login" });
    }
  },
  component: AppLayout,
});

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: HomeRedirect,
});

const billingDashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing",
  beforeLoad: requirePermission(["view-invoices"]),
  component: BillingDashboardPage,
});

const issuersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/issuers",
  beforeLoad: requirePermission(["manage-issuers"]),
  component: IssuersListPage,
});

const comprasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchases",
  beforeLoad: requirePermission(["view-purchases"]),
  component: ComprasPage,
});

const presupuestosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/quotes",
  beforeLoad: requirePermission(["view-invoices"]),
  component: PresupuestosPage,
});

const facturasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/invoices",
  beforeLoad: requirePermission(["view-invoices"]),
  component: FacturasListPage,
});

const facturaNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/invoices/new",
  beforeLoad: requirePermission(["create-invoices", "view-invoices"]),
  component: FacturaFormPage,
});

const clientesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/clients",
  beforeLoad: requirePermission(["manage-clients", "view-invoices"]),
  component: ClientesListPage,
});

const conceptosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/concepts",
  beforeLoad: requirePermission(["view-invoices"]),
  component: ConceptosListPage,
});

const rrhhRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rrhh",
  component: RrhhPage,
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: ProfilePage,
});

const accountingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accounting",
  beforeLoad: requirePermission(["view-accounting"]),
  component: PlanCuentasPage,
});

const accAccountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accounting/accounts",
  beforeLoad: requirePermission(["view-accounting"]),
  component: PlanCuentasPage,
});

const accJournalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accounting/journal",
  beforeLoad: requirePermission(["view-accounting"]),
  component: DiarioPage,
});

const accLedgerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accounting/ledger",
  beforeLoad: requirePermission(["view-mayor", "view-accounting"]),
  component: MayorPage,
});

const accReportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accounting/reports",
  beforeLoad: requirePermission(["view-balance", "view-pyg", "view-accounting"]),
  component: InformesPage,
});

const accVatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accounting/vat",
  beforeLoad: requirePermission(["generate-modelo-303", "view-accounting"]),
  component: Modelo303Page,
});

const accConfigRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accounting/config",
  beforeLoad: requirePermission(["manage-chart-of-accounts", "view-accounting"]),
  component: ConfigContablePage,
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tasks",
  component: TasksPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects",
  component: ProyectosListPage,
  beforeLoad: requirePermission(["view-projects"]),
});

const projectDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$id",
  component: ProyectoDetailPage,
  beforeLoad: requirePermission(["view-projects"]),
});

const automationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/automations",
  component: AutomationPage,
  beforeLoad: requirePermission(["view-invoices", "manage-payroll", "manage-roles"]),
});

const commercialRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/commercial",
  beforeLoad: requirePermission(["view-commercial"]),
  component: CommercialDashboardPage,
});

const commercialValuationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/commercial/valuations",
  beforeLoad: requirePermission(["view-commercial"]),
  component: CommercialValuationsPage,
});

const commercialCostsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/commercial/costs",
  beforeLoad: requirePermission(["manage-commercial-costs"]),
  component: CommercialCostsPage,
});

const commercialBenefitsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/commercial/benefits",
  beforeLoad: requirePermission(["view-commercial-benefits"]),
  component: CommercialBenefitsPage,
});

const commercialSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/commercial/settings",
  beforeLoad: requirePermission(["manage-commercial-settings"]),
  component: CommercialSettingsPage,
});

const mktDashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing",
  beforeLoad: requirePermission(["view-marketing"]),
  component: MarketingDashboardPage,
});

const mktAnalyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/analytics",
  beforeLoad: requirePermission(["view-marketing-analytics", "view-marketing"]),
  component: AnalyticsPage,
});

const mktContactsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/contacts",
  beforeLoad: requirePermission(["view-marketing"]),
  component: ContactosPage,
});

const mktPipelineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/pipeline",
  beforeLoad: requirePermission(["view-marketing"]),
  component: PipelinePage,
});

const mktAgendaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/agenda",
  beforeLoad: requirePermission(["view-marketing"]),
  component: AgendaPage,
});

const mktCampaignsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/campaigns",
  beforeLoad: requirePermission(["manage-campaigns", "view-marketing"]),
  component: CampanasPage,
});

const mktContentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/content",
  beforeLoad: requirePermission(["manage-content", "view-marketing"]),
  component: ContenidosPage,
});

const mktAutomationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/automations",
  beforeLoad: requirePermission(["view-marketing"]),
  component: AutomatizacionesPage,
});

const mktScoringRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/scoring",
  beforeLoad: requirePermission(["view-marketing"]),
  component: ScoringPage,
});

const mktFormsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing/forms",
  beforeLoad: requirePermission(["view-marketing"]),
  component: FormulariosPage,
});

const itRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/it",
  beforeLoad: requirePermission(["view-tickets", "create-tickets"]),
  component: ItPage,
});

const campusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/campus",
  component: () => <PlaceholderPage title="Campus" />,
});

const certificacionesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: CertificacionesPage,
});

const iso900114001Route = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-9001-14001",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: Iso900114001Page,
});

const iso2700127701Route = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-27001-27701",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: Iso2700127701Page,
});

const bibliotecaIso9001Route = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-9001-14001/biblioteca",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaPage standard="iso-9001-14001" />,
});

const bibliotecaIso9001OficinaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-9001-14001/biblioteca/oficina",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaDocumentosPage standard="iso-9001-14001" tipo="oficina" />,
});

const bibliotecaIso9001MedioambienteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-9001-14001/biblioteca/medioambiente",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaDocumentosPage standard="iso-9001-14001" tipo="medioambiente" />,
});

const bibliotecaIso9001ProveedoresRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-9001-14001/biblioteca/proveedores",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaDocumentosPage standard="iso-9001-14001" tipo="proveedores" />,
});

const bibliotecaIso27001Route = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-27001-27701/biblioteca",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaPage standard="iso-27001-27701" />,
});

const bibliotecaIso27001OficinaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-27001-27701/biblioteca/oficina",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaDocumentosPage standard="iso-27001-27701" tipo="oficina" />,
});

const bibliotecaIso27001ProveedoresRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/iso-27001-27701/biblioteca/proveedores",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaDocumentosPage standard="iso-27001-27701" tipo="proveedores" />,
});

// Rutas históricas: siguen disponibles y muestran la Biblioteca de ISO 9001-14001.
const bibliotecaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/biblioteca",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaPage standard="iso-9001-14001" />,
});

const bibliotecaOficinaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/biblioteca/oficina",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaDocumentosPage standard="iso-9001-14001" tipo="oficina" />,
});

const bibliotecaMedioambienteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/biblioteca/medioambiente",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaDocumentosPage standard="iso-9001-14001" tipo="medioambiente" />,
});

const bibliotecaProveedoresRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/certificaciones/biblioteca/proveedores",
  beforeLoad: requirePermission(["view-quality", "manage-quality"]),
  component: () => <BibliotecaDocumentosPage standard="iso-9001-14001" tipo="proveedores" />,
});

const qualityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quality",
  beforeLoad: redirectToIso900114001,
  component: Iso900114001Page,
});

const qualityLegacySectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quality/$section",
  beforeLoad: redirectToIso900114001,
  component: Iso900114001Page,
});

const calidadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calidad",
  beforeLoad: redirectToIso900114001,
  component: Iso900114001Page,
});

const calidadLegacySectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calidad/$section",
  beforeLoad: redirectToIso900114001,
  component: Iso900114001Page,
});

const operativeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/operative",
  component: () => <PlaceholderPage title="Operativa" />,
});

const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notifications",
  component: NotificationsPage,
});

const aiAssistantRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ai",
  beforeLoad: requirePermission(["manage-roles", "view-employees", "view-accounting"]),
  component: AssistantPage,
});

const settingsHomeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  beforeLoad: requirePermission(["manage-roles", "manage-issuers", "edit-employees"]),
  component: ConfiguracionPage,
});

const settingsOrganizationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/organization",
  beforeLoad: requirePermission(["manage-roles", "edit-employees", "manage-issuers"]),
  component: OrganizationPage,
});

const settingsRolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/roles",
  beforeLoad: requirePermission(["manage-roles"]),
  component: RolesPermisosPage,
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/audit",
  beforeLoad: requirePermission(["manage-roles"]),
  component: AuditoriaPage,
});

const settingsTeamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/team",
  beforeLoad: requirePermission(["manage-roles"]),
  component: EquipoPage,
});

const settingsBrandingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/branding",
  beforeLoad: requirePermission(["manage-roles"]),
  component: MarcaPage,
});

const settingsRrhhRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/rrhh",
  beforeLoad: requirePermission(["manage-roles", "edit-employees"]),
  component: RrhhConfigPage,
});

const settingsBillingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/billing",
  beforeLoad: requirePermission(["manage-roles", "manage-issuers"]),
  component: FacturacionConfigPage,
});

const settingsDataCleanupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/data-cleanup",
  beforeLoad: requireRole(["super-admin", "admin", "rrhh", "admin_rrhh"], "/settings"),
  component: DataCleanupPage,
});

// --- Public auth layout ---
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/login",
  component: LoginPage,
});

// --- Landing pública de captación (sin auth) ---
const publicFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/f/$slug",
  component: function PublicForm() {
    const { slug } = publicFormRoute.useParams();
    return <FormularioPublicoPage slug={slug} />;
  },
});

const publicNcpSignatureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firma/ncp/$token",
  component: function PublicNcpSignature() {
    const { token } = publicNcpSignatureRoute.useParams();
    return <NcpSignaturePage token={token} />;
  },
});

const routeTree = rootRoute.addChildren([
  appRoute.addChildren([
    homeRoute,
    adminPanelRoute,
    billingDashboardRoute,
    issuersRoute,
    facturasRoute,
    presupuestosRoute,
    comprasRoute,
    facturaNewRoute,
    clientesRoute,
    conceptosRoute,
    rrhhRoute,
    profileRoute,
    accountingRoute,
    accAccountsRoute,
    accJournalRoute,
    accLedgerRoute,
    accReportsRoute,
    accVatRoute,
    accConfigRoute,
    mktDashboardRoute,
    mktAnalyticsRoute,
    mktContactsRoute,
    mktPipelineRoute,
    mktAgendaRoute,
    mktCampaignsRoute,
    mktContentRoute,
    mktAutomationsRoute,
    mktScoringRoute,
    mktFormsRoute,
    tasksRoute,
    projectsRoute,
    projectDetailRoute,
    automationRoute,
    commercialRoute,
    commercialValuationsRoute,
    commercialCostsRoute,
    commercialBenefitsRoute,
    commercialSettingsRoute,
    itRoute,
    campusRoute,
    certificacionesRoute,
    iso900114001Route,
    iso2700127701Route,
    bibliotecaIso9001Route,
    bibliotecaIso9001OficinaRoute,
    bibliotecaIso9001MedioambienteRoute,
    bibliotecaIso9001ProveedoresRoute,
    bibliotecaIso27001Route,
    bibliotecaIso27001OficinaRoute,
    bibliotecaIso27001ProveedoresRoute,
    bibliotecaRoute,
    bibliotecaOficinaRoute,
    bibliotecaMedioambienteRoute,
    bibliotecaProveedoresRoute,
    qualityRoute,
    qualityLegacySectionRoute,
    calidadRoute,
    calidadLegacySectionRoute,
    operativeRoute,
    notificationsRoute,
    settingsHomeRoute,
    settingsOrganizationRoute,
    settingsRolesRoute,
    settingsAuditRoute,
    settingsTeamRoute,
    settingsBrandingRoute,
    settingsRrhhRoute,
    settingsBillingRoute,
    settingsDataCleanupRoute,
    aiAssistantRoute,
  ]),
  authRoute.addChildren([loginRoute]),
  publicFormRoute,
  publicNcpSignatureRoute,
]);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function Router() {
  return <RouterProvider router={router} />;
}



